<script setup lang="ts">
import { onMounted, ref } from "vue";
import { api, errorText } from "../api";
import DatasetAvatar from "./DatasetAvatar.vue";
import WebsiteIconField from "./WebsiteIconField.vue";
import DatasetImageUpload from "./DatasetImageUpload.vue";
import { CloseIcon, DeleteIcon, SaveIcon } from "../icons";
import { datasetIcons } from "../datasetIcons";
const props = defineProps<{ dataset: any }>();
const metadataRevision = String(props.dataset.metadata_revision);
const emit = defineEmits(["close", "saved", "deleted"]);
const originalIconMode = props.dataset.icon_name
  ? "icon"
  : props.dataset.icon_url
    ? "image"
    : props.dataset.website_url && props.dataset.favicon_status !== "none"
      ? "favicon"
      : props.dataset.favicon_status === "ready"
        ? "upload"
        : "icon";
const dialog = ref<HTMLDialogElement>(),
  settingsTab = ref("general"),
  iconMode = ref(originalIconMode),
  iconUploadId = ref(""),
  uploading = ref(false),
  iconName = ref(props.dataset.icon_name ?? "table"),
  iconUrl = ref(props.dataset.icon_url ?? ""),
  name = ref(props.dataset.name),
  description = ref(props.dataset.description),
  website = ref(props.dataset.website_url ?? ""),
  fetchFavicon = ref(props.dataset.favicon_status !== "none"),
  busy = ref(false),
  error = ref(""),
  preview = ref<any>(),
  confirmName = ref(""),
  confirmArchive = ref(false);
const base = "/api/datasets/" + props.dataset.id;
onMounted(() => dialog.value?.showModal());
async function action(fn: () => Promise<void>) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
function save() {
  if (busy.value || uploading.value) return;
  return action(async () => {
    const body: any = { name: name.value, description: description.value };
    if (iconMode.value === "upload") {
      if (iconUploadId.value) body.iconUploadId = iconUploadId.value;
      else if (originalIconMode !== "upload")
        throw new Error("Vælg et billede, før du gemmer.");
    } else if (
      props.dataset.favicon_status === "failed" ||
      website.value !== (props.dataset.website_url ?? "") ||
      fetchFavicon.value !== (props.dataset.favicon_status !== "none") ||
      iconMode.value !== originalIconMode ||
      iconName.value !== (props.dataset.icon_name ?? "table") ||
      iconUrl.value !== (props.dataset.icon_url ?? "")
    ) {
      body.websiteUrl = website.value || null;
      body.fetchFavicon = iconMode.value === "favicon" && fetchFavicon.value;
      body.iconName = iconMode.value === "icon" ? iconName.value : null;
      body.iconUrl = iconMode.value === "image" ? iconUrl.value : null;
    }
    await api(base, {
      method: "PATCH",
      revision: metadataRevision,
      body,
    });
    emit("saved");
  });
}
function archive() {
  return action(async () => {
    await api(base + "/archive", {
      method: "POST",
      revision: metadataRevision,
      body: { archived: props.dataset.write_state !== "archived" },
    });
    emit("saved");
  });
}
function prepareDelete() {
  return action(async () => {
    preview.value = await api(base + "/deletion-preview");
    confirmName.value = "";
    confirmArchive.value = false;
  });
}
function remove() {
  return action(async () => {
    const op = await api(base, {
      method: "DELETE",
      revision: preview.value.metadataRevision,
      body: {
        confirmation: preview.value.confirmation,
        name: confirmName.value,
      },
    });
    emit("deleted", op);
  });
}
</script>
<template>
  <dialog
    ref="dialog"
    class="record-dialog dataset-settings"
    aria-labelledby="settings-title"
    @cancel.prevent="!busy && emit('close')"
  >
    <div class="dialog-heading">
      <h2 id="settings-title">Datasætindstillinger</h2>
      <button
        class="icon-button"
        :disabled="busy"
        aria-label="Luk indstillinger"
        @click="emit('close')"
      >
        <CloseIcon :size="19" />
      </button>
    </div>
    <nav v-if="!preview" class="tabs settings-tabs" aria-label="Indstillinger">
      <button
        type="button"
        :class="{ active: settingsTab === 'general' }"
        :aria-current="settingsTab === 'general' ? 'page' : undefined"
        @click="settingsTab = 'general'"
      >
        Generelt
      </button>
      <button
        v-if="dataset.canManageLifecycle"
        type="button"
        :class="{ active: settingsTab === 'lifecycle' }"
        :aria-current="settingsTab === 'lifecycle' ? 'page' : undefined"
        @click="settingsTab = 'lifecycle'"
      >
        Arkivér og slet
      </button>
    </nav>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <form
      v-if="!preview && settingsTab === 'general'"
      class="form-stack"
      @submit.prevent="save"
    >
      <label
        >Navn<input v-model="name" required maxlength="120" :disabled="busy"
      /></label>
      <label
        >Beskrivelse<textarea
          v-model="description"
          rows="3"
          maxlength="1000"
          :disabled="busy"
        />
      </label>
      <fieldset class="appearance-field">
        <legend>Datasættets ikon</legend>
        <div class="segmented appearance-modes">
          <button
            v-for="mode in [
              { id: 'icon', label: 'Vælg ikon' },
              { id: 'upload', label: 'Upload billede' },
              { id: 'favicon', label: 'Fra website' },
              { id: 'image', label: 'Billedadresse' },
            ]"
            :key="mode.id"
            type="button"
            :class="{ selected: iconMode === mode.id }"
            :aria-pressed="iconMode === mode.id"
            :disabled="busy"
            @click="
              iconMode = mode.id;
              if (mode.id === 'favicon') fetchFavicon = true;
            "
          >
            {{ mode.label }}
          </button>
        </div>
        <div v-if="iconMode === 'icon'" class="dataset-icon-options">
          <button
            v-for="icon in datasetIcons"
            :key="icon.id"
            type="button"
            :aria-pressed="iconName === icon.id"
            :disabled="busy"
            @click="iconName = icon.id"
          >
            <component :is="icon.component" :size="23" /><span>{{
              icon.label
            }}</span>
          </button>
        </div>
        <DatasetImageUpload
          v-if="iconMode === 'upload'"
          v-model="iconUploadId"
          :dataset="dataset"
          :existing="originalIconMode === 'upload'"
          :disabled="busy"
          @busy="uploading = $event"
        />
        <WebsiteIconField
          :dataset-id="dataset.id"
          v-if="iconMode === 'favicon'"
          v-model:website="website"
          v-model:enabled="fetchFavicon"
          :disabled="busy"
        />
        <label v-if="iconMode === 'image'"
          >Billedadresse<input
            v-model="iconUrl"
            type="url"
            required
            placeholder="https://…/ikon.png"
            :disabled="busy"
          /><span class="muted small"
            >Brug en PNG- eller ICO-fil, fx fra din AI-forbindelse. Højst 512 ×
            512 pixels og 256 KB.</span
          ></label
        >
      </fieldset>
      <p
        v-if="dataset.favicon_status === 'ready' && iconMode !== 'upload'"
        class="muted"
      >
        <DatasetAvatar :dataset="dataset" :size="24" /> Nuværende ikon
      </p>
      <p
        v-if="dataset.favicon_status === 'pending' && iconMode !== 'upload'"
        class="muted"
        role="status"
      >
        Ikonet hentes i baggrunden.
      </p>
      <p
        v-if="dataset.favicon_status === 'failed' && iconMode !== 'upload'"
        class="muted"
      >
        Ikonet kunne ikke hentes. Kontrollér adressen og gem igen.
      </p>
      <div class="form-actions">
        <button
          type="button"
          class="button secondary"
          :disabled="busy"
          @click="emit('close')"
        >
          Annuller</button
        ><button
          class="button primary"
          :disabled="
            busy ||
            uploading ||
            !name.trim() ||
            (iconMode === 'upload' &&
              !iconUploadId &&
              originalIconMode !== 'upload')
          "
        >
          <SaveIcon :size="16" />{{ busy ? "Gemmer…" : "Gem ændringer" }}
        </button>
      </div>
    </form>
    <section
      v-if="
        dataset.canManageLifecycle && !preview && settingsTab === 'lifecycle'
      "
      class="dataset-lifecycle"
      aria-label="Arkivering og sletning"
    >
      <h3>
        {{
          dataset.write_state === "archived"
            ? "Genåbn datasættet"
            : "Arkivér datasættet"
        }}
      </h3>
      <p class="muted">
        {{
          dataset.write_state === "archived"
            ? "Genåbning tillader registrering igen. Tidsplaner skal genstartes særskilt."
            : "Bevar data til læsning og eksport. Registrering, importer og planlagte leveringer stoppes."
        }}
      </p>
      <button
        v-if="!confirmArchive"
        class="button secondary"
        :disabled="busy"
        @click="confirmArchive = true"
      >
        {{
          dataset.write_state === "archived"
            ? "Genåbn datasæt"
            : "Arkivér datasæt"
        }}
      </button>
      <div v-else class="inline-confirm">
        <p>
          {{
            dataset.write_state === "archived"
              ? "Vil du genåbne datasættet?"
              : "Vil du stoppe registrering og planlagte kørsler?"
          }}
        </p>
        <button
          class="button secondary"
          :disabled="busy"
          @click="confirmArchive = false"
        >
          Annuller
        </button>
        <button class="button primary" :disabled="busy" @click="archive">
          {{ dataset.write_state === "archived" ? "Genåbn" : "Arkivér" }}
        </button>
      </div>
      <div class="section-divider" />
      <h3>Slet permanent</h3>
      <p class="muted">
        Fjern datasættet og dets tilknyttede data og filer. Handlingen kan ikke
        fortrydes.
      </p>
      <button
        class="button destructive"
        :disabled="busy"
        @click="prepareDelete"
      >
        <DeleteIcon :size="16" />Slet datasæt permanent
      </button>
    </section>
    <section
      v-if="preview"
      class="form-stack"
      aria-labelledby="delete-dataset-title"
    >
      <h3 id="delete-dataset-title">Slet “{{ preview.name }}” permanent?</h3>
      <p>
        Du mister straks adgangen. Data og filer slettes ved næste natlige
        oprydning. Handlingen kan ikke fortrydes.
      </p>
      <dl class="deletion-counts">
        <div
          v-for="[key, label] in [
            ['records', 'Rækker'],
            ['sources', 'Kilder'],
            ['views', 'Dashboardkort'],
            ['automations', 'Regler'],
            ['report_definitions', 'Rapporter'],
            ['artifacts', 'Eksportfiler'],
          ]"
          :key="key"
        >
          <dt>{{ label }}</dt>
          <dd>{{ preview.counts[key].toLocaleString("da-DK") }}</dd>
        </div>
      </dl>
      <label
        >Skriv “{{ preview.name }}” for at bekræfte<input
          v-model="confirmName"
          autocomplete="off"
          :disabled="busy"
      /></label>
      <div class="form-actions">
        <button
          class="button secondary"
          :disabled="busy"
          @click="preview = undefined"
        >
          Tilbage</button
        ><button
          class="button destructive"
          :disabled="busy || confirmName !== preview.name"
          @click="remove"
        >
          {{ busy ? "Markerer til sletning…" : "Slet permanent" }}
        </button>
      </div>
    </section>
  </dialog>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/DatasetSettings.scss"
></style>
